import { AccordionDetails, AccordionSummary, Box, Typography, withStyles } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { FC } from 'react';
import { Field, useFormState } from 'react-final-form';
import { FieldRadioButton, StyledAccordion, ValidationError } from '../../components';
import { useShopConfig } from '../../hooks/shopConfig';
import { defaultTreetStyles } from '../../shopConfig/config';
import { findOptionsForSelectFilter } from '../../util/search';
import { composeValidators, required, validOption } from '../../util/validators';
import Heading from './Heading';
import Subheading from './Subheading';
import { ListingFlowFormField } from './ListingFlowTypes';
import css from './EditListingPage.module.css';
import { GenericFilterOption } from '../../types/filters/filters';
import { ConditionOption } from '../../types/shopConfig/shopConfigV2';

// Apply styles outside of css modules in order to correctly target MUI styling
const styles = {
  accordion: {
    padding: '2px',
    boxShadow: 'none',
    border: '1px solid rgba(0,0,0,0.1)',
    color: defaultTreetStyles.gray80,
  },
};

interface ConditionProps {
  classes: any;
}

const Condition: FC<ConditionProps> = (props) => {
  const { classes } = props;

  const { listingFlowConfig, filters, conditionOptionsOverride } = useShopConfig();
  const { values } = useFormState();
  const { condition, itemType } = values;
  const { conditionsHelperText } = listingFlowConfig;

  let conditionOptions = findOptionsForSelectFilter('condition', filters);
  if (conditionOptionsOverride) {
    // Filter for the relevant item type conditions override
    conditionOptions = conditionOptionsOverride.filter((option: ConditionOption) => {
      if (!itemType || !option.includedTypes) return true;
      return option.includedTypes.includes(itemType);
    });
  }

  return (
    <>
      <Heading>Condition</Heading>
      <Subheading>{conditionsHelperText}</Subheading>
      <Box display="flex" flexDirection="column" maxWidth="426px">
        <Field
          name={ListingFlowFormField.Condition}
          validate={composeValidators(
            required('Please choose a condition.'),
            validOption(
              conditionOptions.map((option: GenericFilterOption) => option.key),
              'Please choose a valid condition.'
            )
          )}
        >
          {({ input, meta }) => (
            <>
              {/* Setting id here so that form can scroll to it on error */}
              <input
                {...input}
                id={ListingFlowFormField.Condition}
                className={css.hiddenInput}
                type="hidden"
                aria-labelledby="condition-heading"
                aria-describedby="condition-description"
              />
              <ValidationError fieldMeta={meta} />
              {conditionOptions.map(
                ({ key, label, description, hideConditionFromListingFlow }: GenericFilterOption) =>
                  !hideConditionFromListingFlow && (
                    <StyledAccordion
                      key={key}
                      isSelected={condition === key}
                      className={classes.accordion}
                      onChange={() => input.onChange(key)}
                      square
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <FieldRadioButton
                          key={key}
                          id={key}
                          name={key}
                          value={key}
                          checked={condition === key}
                          aria-labelledby={`label-${key}`}
                          labelClassName={css.radioButton}
                        />
                        <Typography
                          id={`label-${key}`}
                          variant="body1"
                          style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}
                        >
                          {label}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2">{description}</Typography>
                      </AccordionDetails>
                    </StyledAccordion>
                  )
              )}
            </>
          )}
        </Field>
      </Box>
    </>
  );
};

export default withStyles(styles)(Condition);
